/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import type { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import { register } from 'ol/proj/proj4';
import { Fill, Stroke, Style as OpenLayersStyle } from 'ol/style';
import type { StyleLike } from 'ol/style/Style';
import proj4 from 'proj4';
import React from 'react';
import type { Dimension, STACGeoTIFFLayerOptions } from '@opengeoweb/webmap';
import { GeoTIFFLayer } from './GeoTIFFLayer';
import { OpenLayersFeatureLayer } from './OpenLayersFeatureLayer';
import type { OpenLayersFeatureLayerProps } from './OpenLayersFeatureLayer';
import {
  STAC_GEOTIFF_FRAME_PRELOAD_RADIUS,
  STAC_ITEM_LOOKUP_WINDOW_INTERVAL_MULTIPLIER,
  STAC_ITEM_LOOKUP_WINDOW_MS,
  STAC_TILED_ITEM_PAGE_LIMIT,
  STAC_TILED_OVERVIEW_ITEM_PAGE_LIMIT,
  getFrameStepInMilliseconds,
  getLookupStepInMilliseconds,
  getRequiredLookupMarginInMilliseconds,
  resolveStacItemQueryState,
  getSingleStacRasterItem,
  getStacFrameItemsToRender,
  getStacFrameTargetTimes,
  getStacItemLookupWindowForTimes,
  getTimeIntervalInMilliseconds,
  isStacItemLookupWindowValidForTime,
  resolvePreferredStyleName,
  shouldPreloadStacFrames,
} from './TimeAwareGeoTIFFLayerUtils';
import type {
  STACFrameItem,
  STACItemLookupWindow,
} from './TimeAwareGeoTIFFLayerUtils';
import {
  STAC_TILED_MINIMUM_ZOOM,
  useStacViewportBbox,
} from './useStacViewportBbox';
import {
  STAC_ITEM_SEARCH_FIELDS_CONFORMANCE,
  getStacRasterBands,
} from '../../../geotiff-layer-api/api';
import {
  useStacCatalogRoot,
  useStacItems,
  useStacCollection,
  useStacOverviewItems,
  useStacTemporalStepItems,
} from '../../../geotiff-layer-api/hooks';
import {
  getStacCollectionStyles,
  resolveStacAssetKey,
  stacColormapToOlStyle,
} from '../../../geotiff-layer-api/utils/stacColormapToOlStyle';
import type { STACLegendMetadata } from '../../../geotiff-layer-api/utils/stacLegendMetadata';
import { getStacGrayscaleSampleRange } from '../../../geotiff-layer-api/utils/stacLegendUtils';
import { isSpatiallyTiledStacCollection } from '../../../geotiff-layer-api/utils/stacTimeStepUtils';
import { useGetStacGeoTIFFLayerInstance } from '../../../geotiff-layer-api/utils/useGetStacGeoTIFFLayerInstance';
import MapContext from '../context/MapContext';
import TimeContext from '../context/TimeContext';
import { getDimensionParamsForWMS } from '../utils/makeDimValuesToPrefetch';
import type { OnInitializeLayerProps } from '../utils/types';

export { getStacFrameItemsToRender, getStacItemLookupWindowForTimes };

const STAC_LOOKUP_HALF_WINDOW_MS = 30 * 60 * 1000;
const STAC_TILED_LOOKUP_WINDOW_MS = STAC_LOOKUP_HALF_WINDOW_MS * 2;
const STAC_OVERVIEW_TIME_TOLERANCE_MS = STAC_LOOKUP_HALF_WINDOW_MS;
const STAC_TILED_TIMELINE_TOLERANCE_MS = STAC_LOOKUP_HALF_WINDOW_MS;

const stacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(0, 145, 234, 0.08)' }),
  stroke: new Stroke({ color: 'rgba(0, 145, 234, 0.9)', width: 2 }),
});
const hoveredStacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(0, 145, 234, 0.2)' }),
  stroke: new Stroke({ color: 'rgba(0, 145, 234, 1)', width: 3 }),
});
const hoveredRasterStacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
  stroke: new Stroke({ color: 'rgba(0, 145, 234, 1)', width: 3 }),
});
const selectedStacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(255, 193, 7, 0)' }),
  stroke: new Stroke({ color: 'rgba(255, 193, 7, 1)', width: 3 }),
});
const hoveredSelectedStacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(255, 193, 7, 0.2)' }),
  stroke: new Stroke({ color: 'rgba(255, 193, 7, 1)', width: 3 }),
});
const hoveredSelectedRasterStacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
  stroke: new Stroke({ color: 'rgba(255, 193, 7, 1)', width: 3 }),
});
const transparentStacFootprintStyle = new OpenLayersStyle({
  fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
  stroke: new Stroke({ color: 'rgba(0, 0, 0, 0)', width: 1 }),
});

const bboxesIntersect = (first: number[], second: number[]): boolean =>
  first.length === 4 &&
  second.length === 4 &&
  first[0] <= second[2] &&
  first[2] >= second[0] &&
  first[1] <= second[3] &&
  first[3] >= second[1];

interface ManuallySelectedStacTile {
  bbox: number[];
  id: string;
}

interface TimeAwareGeoTIFFLayerProps extends OpenLayersFeatureLayerProps {
  layerId?: string;
  stacCollectionUrl: string;
  layerName: string;
  styleName?: string;
  dimensions?: Dimension[];
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  opacity?: number;
  visible?: boolean;
  assetKey?: string;
  dataLayout?: STACGeoTIFFLayerOptions['dataLayout'];
  spatialFilter?: boolean;
  temporalStep?: string;
}

const useStacItemsQueryStrategy = ({
  collectionData,
  isItemQueryEnabled,
  isTiledCollection,
  isTiledOverview,
  itemEndDate,
  itemQueryBbox,
  itemStartDate,
  stacCollectionUrl,
}: {
  collectionData: ReturnType<typeof useStacCollection>['data'];
  isItemQueryEnabled: boolean;
  isTiledCollection: boolean;
  isTiledOverview: boolean;
  itemEndDate: string | undefined;
  itemQueryBbox: number[] | undefined;
  itemStartDate: string | undefined;
  stacCollectionUrl: string;
}): {
  items: ReturnType<typeof useStacItems>['data'];
  isFetching: boolean;
} => {
  const stacRootUrl = collectionData?.links.find(
    ({ rel }) => rel === 'root',
  )?.href;
  const catalogRootQuery = useStacCatalogRoot(
    stacRootUrl ?? '',
    isTiledOverview,
  );
  const itemSearchUrl = catalogRootQuery.data?.links.find(
    ({ rel, method }) =>
      rel === 'search' && (!method || method.toUpperCase() === 'GET'),
  )?.href;
  const conformsToItemSearchFields = catalogRootQuery.data?.conformsTo?.some(
    (conformance) =>
      conformance.replace(/\/$/, '') === STAC_ITEM_SEARCH_FIELDS_CONFORMANCE,
  );
  const supportsItemSearchFields = Boolean(
    itemSearchUrl && conformsToItemSearchFields,
  );
  const isOverviewCapabilityResolved =
    !stacRootUrl || catalogRootQuery.isSuccess || catalogRootQuery.isError;
  const canUseLightweightOverview = isTiledOverview && supportsItemSearchFields;

  const overviewItemsQuery = useStacOverviewItems(
    itemSearchUrl ?? '',
    collectionData?.id ?? '',
    itemStartDate,
    itemEndDate,
    itemQueryBbox,
    isItemQueryEnabled && canUseLightweightOverview,
    STAC_TILED_OVERVIEW_ITEM_PAGE_LIMIT,
  );
  const shouldUseLightweightOverview =
    canUseLightweightOverview && !overviewItemsQuery.isError;
  const shouldUseFullItemQuery =
    !isTiledOverview ||
    (isTiledOverview &&
      isOverviewCapabilityResolved &&
      !shouldUseLightweightOverview);

  const fullItemsQuery = useStacItems(
    stacCollectionUrl,
    itemStartDate,
    itemEndDate,
    itemQueryBbox,
    isItemQueryEnabled && shouldUseFullItemQuery,
    isTiledCollection ? STAC_TILED_ITEM_PAGE_LIMIT : undefined,
  );

  const { data: items, isFetching } = shouldUseLightweightOverview
    ? overviewItemsQuery
    : fullItemsQuery;

  return { items, isFetching };
};

const useRetainedTiledItems = ({
  isFetching,
  isTiledCollection,
  isTiledOverview,
  items,
  timeValue,
}: {
  isFetching: boolean;
  isTiledCollection: boolean;
  isTiledOverview: boolean;
  items: ReturnType<typeof useStacItems>['data'];
  timeValue?: string;
}): {
  displayedItems: ReturnType<typeof useStacItems>['data'];
  canRetainRasterWhileFetching: boolean;
} => {
  const lastTiledItemsRef = React.useRef<
    | {
        items: NonNullable<typeof items>;
        timeValue?: string;
      }
    | undefined
  >(undefined);

  React.useEffect(() => {
    if (isTiledCollection && !isTiledOverview && items && items.length > 0) {
      lastTiledItemsRef.current = { items, timeValue };
    }
  }, [isTiledCollection, isTiledOverview, items, timeValue]);

  const lastTiledItems = lastTiledItemsRef.current;
  const retainedTiledItems =
    !isTiledOverview &&
    lastTiledItems &&
    lastTiledItems.timeValue === timeValue &&
    isFetching
      ? lastTiledItems.items
      : undefined;
  const displayedItems = items && items.length > 0 ? items : retainedTiledItems;
  const canRetainRasterWhileFetching = Boolean(retainedTiledItems);

  return { displayedItems, canRetainRasterWhileFetching };
};

export const TimeAwareGeoTIFFLayer: React.FC<TimeAwareGeoTIFFLayerProps> = ({
  stacCollectionUrl,
  layerName,
  style,
  styleName,
  zIndex,
  opacity,
  dimensions = [],
  layerId,
  visible = true,
  assetKey,
  dataLayout = 'auto',
  spatialFilter,
  temporalStep,
  onInitializeLayer,
}) => {
  const { prefetchList = [] } = React.useContext(TimeContext);
  const { map } = React.useContext(MapContext);
  const [manuallySelectedTile, setManuallySelectedTile] =
    React.useState<ManuallySelectedStacTile>();
  const { data: collectionData } = useStacCollection(stacCollectionUrl);
  const temporalInterval = collectionData?.extent?.temporal?.interval?.[0];
  const sampleStart = temporalInterval?.[0] ?? undefined;
  const fallbackSampleEnd = React.useMemo(
    () => (collectionData ? new Date().toISOString() : undefined),
    [collectionData],
  );
  const sampleEnd = temporalInterval?.[1] ?? fallbackSampleEnd;
  const layoutSample = useStacTemporalStepItems(
    stacCollectionUrl,
    sampleStart,
    sampleEnd,
    dataLayout === 'auto',
  );
  const isTiledCollection = isSpatiallyTiledStacCollection(
    dataLayout,
    Boolean(collectionData?.item_assets),
    layoutSample.data,
  );
  const effectiveDataLayout = isTiledCollection ? 'spatial-tiles' : 'temporal';
  const wmLayer = useGetStacGeoTIFFLayerInstance(
    stacCollectionUrl,
    layerName,
    layerId,
    onInitializeLayer,
    temporalStep,
    effectiveDataLayout,
  );

  const effectiveStyleName = React.useMemo(() => {
    const stacStyles = getStacCollectionStyles(
      collectionData?.renders,
      collectionData?.item_assets,
    ) as { name: string }[];
    const styleNames: string[] = stacStyles.map((stacStyle) => stacStyle.name);
    return resolvePreferredStyleName(styleNames, styleName);
  }, [collectionData, styleName]);
  const renderEntry = collectionData?.renders?.[effectiveStyleName];

  const geotiffStyle = React.useMemo(() => {
    if (renderEntry?.colormap) {
      return stacColormapToOlStyle(renderEntry.colormap);
    }

    return undefined;
  }, [renderEntry?.colormap]);

  const shouldNormalizeGeoTiff = !renderEntry?.colormap;

  const shouldUseSpatialFilter = spatialFilter ?? isTiledCollection;
  const viewportBbox = useStacViewportBbox(map, shouldUseSpatialFilter);
  const selectionViewportBbox = useStacViewportBbox(
    map,
    shouldUseSpatialFilter,
    undefined,
    0,
  );
  React.useEffect(() => {
    if (
      manuallySelectedTile &&
      selectionViewportBbox &&
      !bboxesIntersect(manuallySelectedTile.bbox, selectionViewportBbox)
    ) {
      setManuallySelectedTile(undefined);
    }
  }, [manuallySelectedTile, selectionViewportBbox]);
  const rasterViewportBbox = useStacViewportBbox(
    map,
    shouldUseSpatialFilter,
    isTiledCollection ? STAC_TILED_MINIMUM_ZOOM : undefined,
  );
  const { isTiledOverview, itemQueryBbox, isItemQueryEnabled } =
    resolveStacItemQueryState({
      hasCollectionData: Boolean(collectionData),
      isTiledCollection,
      rasterViewportBbox,
      shouldUseSpatialFilter,
      viewportBbox,
    });

  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);
  const requestedTimeValue = dimensions.find(
    ({ name }) => name?.toLowerCase() === 'time',
  )?.currentValue;
  // Third-party tiled STAC collections expose a temporal extent, not a list of
  // discrete valid times. WMS closest-value logic can therefore snap the
  // timeline to an unrelated acquisition; query STAC with the exact selection.
  const timeValue = isTiledCollection
    ? requestedTimeValue
    : dimensionsValues?.['TIME'];
  React.useEffect(() => {
    setManuallySelectedTile(undefined);
  }, [isTiledCollection, timeValue]);
  const timeIntervalMs = getTimeIntervalInMilliseconds(
    wmLayer?.getDimension('time')?.getDimInterval(),
  );
  const prefetchListEffectCheck = prefetchList.join(',');
  const frameStepMs = React.useMemo(
    () =>
      isTiledCollection
        ? undefined
        : getFrameStepInMilliseconds(timeValue, prefetchList, timeIntervalMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTiledCollection, prefetchListEffectCheck, timeIntervalMs, timeValue],
  );
  const lookupStepMs = isTiledCollection
    ? undefined
    : getLookupStepInMilliseconds(frameStepMs, timeIntervalMs);
  const shouldPreloadFrames = shouldPreloadStacFrames(
    frameStepMs,
    timeIntervalMs,
  );
  const preloadRadius =
    shouldPreloadFrames && !isTiledCollection
      ? STAC_GEOTIFF_FRAME_PRELOAD_RADIUS
      : 0;
  let lookupWindowMs = STAC_ITEM_LOOKUP_WINDOW_MS;
  if (isTiledCollection) {
    lookupWindowMs = STAC_TILED_LOOKUP_WINDOW_MS;
  } else if (lookupStepMs) {
    lookupWindowMs = lookupStepMs * STAC_ITEM_LOOKUP_WINDOW_INTERVAL_MULTIPLIER;
  }
  const frameTargetTimes = React.useMemo(
    () =>
      getStacFrameTargetTimes(
        timeValue,
        prefetchList,
        frameStepMs,
        preloadRadius,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [frameStepMs, prefetchListEffectCheck, preloadRadius, timeValue],
  );
  const requiredLookupMarginMs = getRequiredLookupMarginInMilliseconds(
    timeValue,
    frameTargetTimes,
    lookupStepMs ? lookupStepMs / 2 : lookupWindowMs / 2,
  );

  const [requestedStartDate, requestedEndDate] = React.useMemo(
    () =>
      getStacItemLookupWindowForTimes(
        timeValue ? [timeValue] : [],
        lookupWindowMs,
      ),
    [timeValue, lookupWindowMs],
  );

  const isTiledLookupWindow = isTiledCollection;
  const [activeItemLookupWindow, setActiveItemLookupWindow] =
    React.useState<STACItemLookupWindow>([undefined, undefined]);
  const isActiveItemLookupWindowValid = isStacItemLookupWindowValidForTime(
    activeItemLookupWindow,
    timeValue,
    requiredLookupMarginMs,
  );
  const [activeStartDate, activeEndDate] = activeItemLookupWindow;
  const startDate =
    !isTiledLookupWindow && isActiveItemLookupWindowValid
      ? activeStartDate
      : requestedStartDate;
  const endDate =
    !isTiledLookupWindow && isActiveItemLookupWindowValid
      ? activeEndDate
      : requestedEndDate;

  React.useEffect(() => {
    if (isTiledLookupWindow) {
      return;
    }

    setActiveItemLookupWindow((currentWindow) => {
      if (
        isStacItemLookupWindowValidForTime(
          currentWindow,
          timeValue,
          requiredLookupMarginMs,
        )
      ) {
        return currentWindow;
      }

      if (
        currentWindow[0] === requestedStartDate &&
        currentWindow[1] === requestedEndDate
      ) {
        return currentWindow;
      }

      return [requestedStartDate, requestedEndDate];
    });
  }, [
    isTiledLookupWindow,
    requestedEndDate,
    requestedStartDate,
    requiredLookupMarginMs,
    timeValue,
  ]);

  const [itemStartDate, itemEndDate] = React.useMemo(() => {
    if (isTiledCollection && timeValue) {
      return getStacItemLookupWindowForTimes(
        [timeValue],
        STAC_TILED_LOOKUP_WINDOW_MS,
      );
    }
    return [startDate, endDate];
  }, [endDate, isTiledCollection, startDate, timeValue]);

  // When a tile is manually selected, fetch its full item data
  // This works in both overview mode and zoomed-in mode
  const isManualSelectionActive = manuallySelectedTile;
  const manualSelectionItemsQuery = useStacItems(
    stacCollectionUrl,
    itemStartDate,
    itemEndDate,
    manuallySelectedTile?.bbox,
    isManualSelectionActive && Boolean(itemStartDate && itemEndDate),
    STAC_TILED_ITEM_PAGE_LIMIT,
  );

  const { items, isFetching } = useStacItemsQueryStrategy({
    collectionData,
    isItemQueryEnabled,
    isTiledCollection,
    isTiledOverview,
    itemEndDate,
    itemQueryBbox,
    itemStartDate,
    stacCollectionUrl,
  });

  const { displayedItems, canRetainRasterWhileFetching } =
    useRetainedTiledItems({
      isFetching,
      isTiledCollection,
      isTiledOverview,
      items,
      timeValue,
    });

  const timelineSelectionItems = React.useMemo(() => {
    if (
      !isTiledCollection ||
      isTiledOverview ||
      !displayedItems ||
      !timeValue
    ) {
      return displayedItems;
    }

    const timelineTime = new Date(timeValue).getTime();
    if (!Number.isFinite(timelineTime)) {
      return displayedItems;
    }

    return displayedItems.filter((item) => {
      const itemTime = new Date(item.properties.datetime ?? '').getTime();
      return (
        Number.isFinite(itemTime) &&
        Math.abs(itemTime - timelineTime) <= STAC_TILED_TIMELINE_TOLERANCE_MS
      );
    });
  }, [displayedItems, isTiledCollection, isTiledOverview, timeValue]);

  const [tiledRasterAnchorTime, setTiledRasterAnchorTime] = React.useState<
    string | undefined
  >(undefined);
  React.useEffect(() => {
    if (!isTiledCollection) {
      return;
    }

    setTiledRasterAnchorTime(undefined);
  }, [isTiledCollection, timeValue]);
  React.useEffect(() => {
    if (isTiledCollection && isTiledOverview) {
      setTiledRasterAnchorTime(undefined);
    }
  }, [isTiledCollection, isTiledOverview]);

  const frameSelectionTimeValue =
    isTiledCollection && !isTiledOverview
      ? (tiledRasterAnchorTime ?? timeValue)
      : timeValue;

  const isWaitingForRasterQuery =
    isTiledCollection &&
    !isTiledOverview &&
    !canRetainRasterWhileFetching &&
    (!items || isFetching);
  const isWaitingForManualSelectionQuery =
    isManualSelectionActive && manualSelectionItemsQuery.isFetching;
  const isWaitingForRasterSelection =
    isTiledCollection && !isTiledOverview && !selectionViewportBbox;
  const shouldRenderFootprints =
    (isTiledOverview && !isManualSelectionActive) ||
    isWaitingForRasterQuery ||
    isWaitingForManualSelectionQuery ||
    isWaitingForRasterSelection;

  const frameItems = React.useMemo<STACFrameItem[]>(
    () =>
      getStacFrameItemsToRender(
        timelineSelectionItems,
        frameSelectionTimeValue,
        frameTargetTimes,
        selectionViewportBbox,
        isTiledOverview ? STAC_OVERVIEW_TIME_TOLERANCE_MS : undefined,
        isTiledCollection ? STAC_TILED_LOOKUP_WINDOW_MS : undefined,
      ),
    [
      frameSelectionTimeValue,
      frameTargetTimes,
      isTiledCollection,
      isTiledOverview,
      selectionViewportBbox,
      timelineSelectionItems,
    ],
  );
  const rasterFrameItems: STACFrameItem[] = React.useMemo(() => {
    // When a tile is manually selected, use the separately fetched full item data
    if (isManualSelectionActive && manualSelectionItemsQuery.data?.length) {
      // Filter to only the selected item (query might return overlapping tiles)
      // Match by ID first, then verify bbox if needed
      const selectedItem = manualSelectionItemsQuery.data.find(
        (item) => item.id === manuallySelectedTile?.id,
      );
      if (selectedItem) {
        return [{ item: selectedItem, isSelected: true }];
      }
      // Fallback: if no exact ID match, try to find by bbox (shouldn't happen in normal cases)
      const bboxMatch = manualSelectionItemsQuery.data.find((item) => {
        if (!manuallySelectedTile?.bbox || !item.bbox) {
          return false;
        }
        return (
          item.bbox[0] === manuallySelectedTile.bbox[0] &&
          item.bbox[1] === manuallySelectedTile.bbox[1] &&
          item.bbox[2] === manuallySelectedTile.bbox[2] &&
          item.bbox[3] === manuallySelectedTile.bbox[3]
        );
      });
      return bboxMatch ? [{ item: bboxMatch, isSelected: true }] : [];
    }

    if (isTiledCollection && !isTiledOverview) {
      return getSingleStacRasterItem(
        frameItems,
        selectionViewportBbox,
        undefined,
      );
    }

    return frameItems;
  }, [
    frameItems,
    isTiledCollection,
    isTiledOverview,
    isManualSelectionActive,
    manuallySelectedTile?.id,
    manuallySelectedTile?.bbox,
    manualSelectionItemsQuery.data,
    selectionViewportBbox,
  ]);

  React.useEffect(() => {
    if (
      manuallySelectedTile &&
      displayedItems &&
      !isFetching &&
      !manualSelectionItemsQuery.isFetching &&
      !manualSelectionItemsQuery.data?.some(
        ({ id }) => id === manuallySelectedTile.id,
      )
    ) {
      setManuallySelectedTile(undefined);
    }
  }, [
    displayedItems,
    isFetching,
    manualSelectionItemsQuery.data,
    manualSelectionItemsQuery.isFetching,
    manuallySelectedTile,
  ]);

  React.useEffect(() => {
    if (!isTiledCollection || isTiledOverview) {
      return;
    }

    const selectedRasterTime = rasterFrameItems.find(
      ({ isSelected }) => isSelected,
    )?.item.properties.datetime;
    if (typeof selectedRasterTime !== 'string') {
      return;
    }
    const selectedRasterEpoch = new Date(selectedRasterTime ?? '').getTime();
    if (!Number.isFinite(selectedRasterEpoch)) {
      return;
    }

    setTiledRasterAnchorTime((currentTime) =>
      currentTime === selectedRasterTime ? currentTime : selectedRasterTime,
    );
  }, [isTiledCollection, isTiledOverview, rasterFrameItems]);
  const rasterAssetKeys = React.useMemo(() => {
    // When manually selected, fetch assets for the selected item
    if (isManualSelectionActive && manualSelectionItemsQuery.data?.length) {
      // Find the item matching the selected tile id (query might return overlapping tiles)
      const selectedItem = manualSelectionItemsQuery.data.find(
        (item) => item.id === manuallySelectedTile?.id,
      );
      if (!selectedItem) {
        return new Map<string, string>();
      }
      const resolvedAssetKey = resolveStacAssetKey(
        selectedItem.assets ?? {},
        effectiveStyleName,
        renderEntry?.assets,
        assetKey,
      );
      return resolvedAssetKey
        ? new Map([[selectedItem.id, resolvedAssetKey]])
        : new Map<string, string>();
    }

    if (isTiledOverview) {
      return new Map<string, string>();
    }

    return new Map(
      rasterFrameItems.flatMap(({ item }) => {
        const resolvedAssetKey = resolveStacAssetKey(
          item.assets ?? {},
          effectiveStyleName,
          renderEntry?.assets,
          assetKey,
        );
        return resolvedAssetKey ? [[item.id, resolvedAssetKey]] : [];
      }),
    );
  }, [
    isManualSelectionActive,
    manualSelectionItemsQuery.data,
    isTiledOverview,
    rasterFrameItems,
    effectiveStyleName,
    renderEntry?.assets,
    assetKey,
    manuallySelectedTile?.id,
  ]);
  const rasterItemIds = React.useMemo(
    () =>
      new Set(
        rasterFrameItems
          .map(({ item }) => item.id)
          .filter((itemId) => rasterAssetKeys.has(itemId)),
      ),
    [rasterAssetKeys, rasterFrameItems],
  );
  const getFrameBand = React.useCallback(
    (item: STACFrameItem['item'], resolvedAssetKey: string) =>
      getStacRasterBands(item, resolvedAssetKey)?.[0] ??
      collectionData?.item_assets?.[resolvedAssetKey]?.['raster:bands']?.[0] ??
      collectionData?.summaries?.['raster:bands']?.[0],
    [collectionData],
  );
  const activeLegendMetadata = React.useMemo<
    STACLegendMetadata | undefined
  >(() => {
    if (!visible || shouldRenderFootprints) {
      return undefined;
    }
    const activeFrame = rasterFrameItems.find(
      ({ item, isSelected }) =>
        isSelected && item.assets?.[rasterAssetKeys.get(item.id) ?? ''],
    );
    const activeAssetKey =
      activeFrame && rasterAssetKeys.get(activeFrame.item.id);
    return activeFrame && activeAssetKey
      ? {
          assetKey: activeAssetKey,
          band: getFrameBand(activeFrame.item, activeAssetKey),
        }
      : undefined;
  }, [
    visible,
    shouldRenderFootprints,
    rasterFrameItems,
    rasterAssetKeys,
    getFrameBand,
  ]);
  React.useEffect(() => {
    if (!wmLayer || !activeLegendMetadata) {
      return undefined;
    }
    wmLayer.stacLegendMetadata = activeLegendMetadata;
    return (): void => {
      if (wmLayer.stacLegendMetadata === activeLegendMetadata) {
        wmLayer.stacLegendMetadata = undefined;
      }
    };
  }, [wmLayer, activeLegendMetadata]);
  const footprintStyle = React.useMemo<StyleLike>(
    () =>
      (feature: FeatureLike): OpenLayersStyle => {
        const itemId = feature.get('stacItemId') as string | undefined;
        const isHovered = feature.get('hover') === true;
        const hasRasterData = itemId && rasterItemIds.has(itemId);
        if (itemId === manuallySelectedTile?.id) {
          if (!isHovered) {
            return selectedStacFootprintStyle;
          }
          return hasRasterData
            ? hoveredSelectedRasterStacFootprintStyle
            : hoveredSelectedStacFootprintStyle;
        }
        if (isHovered) {
          return hasRasterData
            ? hoveredRasterStacFootprintStyle
            : hoveredStacFootprintStyle;
        }
        return hasRasterData
          ? transparentStacFootprintStyle
          : stacFootprintStyle;
      },
    [manuallySelectedTile?.id, rasterItemIds],
  );
  const clickOnFootprint = React.useMemo(
    () => ({
      eventType: 'singleclick' as const,
      onClick: (features: Feature[]): void => {
        const clickedFeature = features.find((feature) =>
          Boolean(feature.get('stacItemId')),
        );
        const itemId = clickedFeature?.get('stacItemId') as string | undefined;
        const bbox = clickedFeature?.get('stacItemBbox') as
          | number[]
          | undefined;
        if (
          !itemId ||
          bbox?.length !== 4 ||
          !bbox.every((coordinate) => Number.isFinite(coordinate))
        ) {
          return;
        }

        setManuallySelectedTile((currentTile) =>
          currentTile?.id === itemId
            ? undefined
            : { bbox: [...bbox], id: itemId },
        );
      },
    }),
    [],
  );
  const hoverOnFootprint = React.useMemo(() => ({ active: true }), []);

  React.useLayoutEffect(() => {
    frameItems.forEach(({ item }) => {
      const wkt2 = item.properties['proj:wkt2'];

      if (wkt2) {
        registerProjection(wkt2);
      }
    });

    // Also register projection for manually selected item
    if (isManualSelectionActive && manualSelectionItemsQuery.data?.length) {
      const selectedItem = manualSelectionItemsQuery.data.find(
        (item) => item.id === manuallySelectedTile?.id,
      );
      if (selectedItem) {
        const wkt2 = selectedItem.properties['proj:wkt2'];
        if (wkt2) {
          registerProjection(wkt2);
        }
      }
    }
  }, [
    frameItems,
    isManualSelectionActive,
    manualSelectionItemsQuery.data,
    manuallySelectedTile?.id,
  ]);

  if (!displayedItems || !startDate || !endDate) {
    return null;
  }

  if (
    !isStacItemLookupWindowValidForTime(
      [startDate, endDate],
      timeValue,
      requiredLookupMarginMs,
    )
  ) {
    return null;
  }

  if (frameItems.length === 0) {
    return null;
  }

  const footprintFeatureCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: frameItems
      .filter(({ item }) => Boolean(item.geometry))
      .map(({ item }) => ({
        type: 'Feature',
        id: item.id,
        geometry: item.geometry!,
        properties: {
          datetime: item.properties.datetime,
          stacItemBbox: item.bbox,
          stacItemId: item.id,
        },
      })),
  };
  const footprintLayer = isTiledCollection ? (
    <OpenLayersFeatureLayer
      clickOnFeature={clickOnFootprint}
      featureCollection={footprintFeatureCollection}
      hoverSelect={hoverOnFootprint}
      opacity={opacity}
      style={footprintStyle}
      testId="stac-item-footprints"
      visible={visible}
      zIndex={(zIndex ?? 0) + 1}
    />
  ) : null;

  if (shouldRenderFootprints) {
    return footprintLayer;
  }

  return (
    <>
      {footprintLayer}
      {rasterFrameItems.map(({ item, isSelected }) => {
        const resolvedAssetKey = rasterAssetKeys.get(item.id);
        if (!resolvedAssetKey) {
          return null;
        }
        const resolvedAsset = item.assets?.[resolvedAssetKey];
        if (!resolvedAsset) {
          return null;
        }
        const band = getFrameBand(item, resolvedAssetKey);
        const sampleRange = getStacGrayscaleSampleRange(
          renderEntry ?? { assets: [resolvedAssetKey] },
          band,
          Object.values(collectionData?.['cube:variables'] ?? {})[0],
        );
        const nodata = renderEntry?.nodata ?? band?.nodata;

        const projection =
          item.properties['proj:wkt2'] ??
          item.properties['proj:code'] ??
          (item.properties['proj:epsg']
            ? `EPSG:${item.properties['proj:epsg']}`
            : undefined);

        return (
          <GeoTIFFLayer
            geotiffStyle={geotiffStyle}
            key={`${item.id}-${resolvedAssetKey}-${effectiveStyleName}`}
            layerName={`${item.id}-${resolvedAssetKey}`}
            layerOptions={{
              opacity: isSelected ? (opacity ?? 1) : 0,
              zIndex: isSelected ? zIndex : (zIndex ?? 0) - 1,
            }}
            layerProps={{
              enabled: visible,
            }}
            max={shouldNormalizeGeoTiff ? sampleRange?.[1] : undefined}
            min={shouldNormalizeGeoTiff ? sampleRange?.[0] : undefined}
            nodata={nodata === undefined ? undefined : Number(nodata)}
            normalize={shouldNormalizeGeoTiff}
            projection={projection}
            url={resolvedAsset.href}
          />
        );
      })}
    </>
  );
};

const registerProjection = (wkt2: string): void => {
  if (!proj4.defs(wkt2)) {
    proj4.defs(wkt2, wkt2);
    register(proj4);
  }
};
